import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Button } from '@mui/material';
import { AUTH0 } from '../../utils/config';
import unifiedApi from '../../utils/unifiedApi';

/**
 * Page to handle invitation acceptance.
 * Users land here from invitation emails/links with a secure token.
 * This page validates the token, gets the email, then triggers Auth0 login.
 */
const AcceptInvitePage = () => {
  const { loginWithRedirect, isAuthenticated, isLoading: auth0Loading } = useAuth0();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [validating, setValidating] = useState(true);
  const [invitationData, setInvitationData] = useState(null);

  useEffect(() => {
    const validateAndInitiate = async () => {
      // Get token from query params
      const token = searchParams.get('token');
      
      if (!token) {
        setError('Invalid invitation link - no token provided');
        setValidating(false);
        return;
      }

      // If user is already authenticated, redirect to dashboard
      if (isAuthenticated) {
        window.location.replace('/dashboard');
        return;
      }

      // Don't validate while Auth0 is still loading
      if (auth0Loading) {
        return;
      }

      try {
        // Validate the token with the API
        const data = await unifiedApi.validateInvitation(token);

        if (!data) {
          setError('This invitation link is invalid or has expired. Please request a new invitation.');
          setValidating(false);
          return;
        }

        setInvitationData(data);
        setValidating(false);

        console.log('Invitation validated for:', data.email);
        
        // Use Auth0 SDK's loginWithRedirect with login_hint
        await loginWithRedirect({
          authorizationParams: {
            redirect_uri: AUTH0.redirectUri,
            login_hint: data.email,
            screen_hint: 'signup',
          }
        });
      } catch (err) {
        console.error('Error validating invitation:', err);
        setError('Failed to validate invitation. Please try again.');
        setValidating(false);
      }
    };

    validateAndInitiate();
  }, [loginWithRedirect, searchParams, isAuthenticated, auth0Loading]);

  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        padding: 3
      }}>
        <Alert severity="error" sx={{ mb: 3, maxWidth: 500 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.location.href = '/'}
        >
          Go to Home
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      flexDirection: 'column' 
    }}>
      <CircularProgress size={60} />
      <Typography variant="h6" sx={{ mt: 2 }}>
        {validating ? 'Validating your invitation...' : 'Preparing your account setup...'}
      </Typography>
      {invitationData?.tenantName && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Joining {invitationData.tenantName}
        </Typography>
      )}
    </Box>
  );
};

export default AcceptInvitePage;
